import {
  ArrowLeft, CalendarPlus, Check, ChevronDown, Mail, MailOpen, MessagesSquare,
  PanelRightClose, PanelRightOpen, Phone, PhoneCall, Star, Trash2,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { Avatar, Tooltip } from "@/components/ui/misc";
import {
  ACTIVITY_TYPES, canCallContact, conversationTitle, filterActivities, firstNewText, messageActivity, RECORD_FILTERS,
  TEXT_FILTERS, threadActivities, timelineKinds, type Activity, type ActivityFilter, type Channel, type Conversation,
  type UnreadAtOpen,
} from "@/lib/conversations";
import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { seesNextCall } from "@/lib/cadence";
import { contactName } from "@/lib/contacts";
import { leadNotFound, useContact, useContactTimeline, useTextMessages } from "@/lib/queries";
import type { LeadCard } from "@/lib/types";
import { cn, formatDateTime, formatPhoneNumber } from "@/lib/utils";
import { isDialable } from "@/lib/phone-number";
import { DialNumberButton } from "@/softphone/dial-number";
import { useSoftphone } from "@/softphone/softphone-context";
import { NewAppointmentDialog } from "@/features/appointment-new";
import { NextAppointment } from "@/features/upcoming-appointments";
import { ActivityRows, ConversationIdContext, RecordContext } from "@/features/conversations/activity-rows";
import { typeIcon } from "@/features/conversations/channel-ui";
import { Composer } from "@/features/conversations/composer";
import { ContactOutcomeDialog } from "@/features/contact-outcome";
import { OpenCallback, useOutcomeLabel } from "@/features/contact-timeline";

/**
 * The open conversation: its header, the timeline of everything that passed between the team and
 * the contact, and the composer.
 *
 * A conversation with a contact on the platform reads its thread from their record's timeline
 * (`GET /leads/{id}/timeline`): calls, notes, stage moves, callbacks and the platform's own entries,
 * the header's filter narrowing it server-side. One without (the development sample) draws the
 * activities it carries.
 *
 * With a record, the thread is also where the team works on it: a comment is a note on the record
 * (edited and removed as on the record), "Set outcome" records one as the record page does, "Book
 * appointment" books the contact on a calendar (ACL-294), and the callback still to be made and the
 * next appointment are pinned above everything so nobody reading the thread misses them.
 *
 * The record's timeline holds every conversation the contact has: a lead with an SMS and an email
 * thread reads both here, the SMS and Email filters telling them apart on the server, and the "New"
 * line placed by the opened conversation's own messages.
 */
export function Thread({
  conversation: c, live = false, texts = true, organize = true, newFrom, unreadAtOpen, preferChannel, panelOpen, onPanel, onBack, onStar, onRead, onDelete,
}: {
  conversation: Conversation;
  /** Read from core-api: the thread asks for its texts rather than drawing the ones it carries. */
  live?: boolean;
  /** Whether the server keeps texts: without, the record's timeline is not asked for them, as
   *  core-api refuses a kind it does not know. */
  texts?: boolean;
  /** Offer star and delete, which only the page keeps (`ORGANIZING_KEPT`). */
  organize?: boolean;
  /** Index of the first activity that was unread when the thread was opened, for the "New" line. */
  newFrom: number | null;
  /** In a thread read from core-api: what was unread when it was opened, for the "New" line. */
  unreadAtOpen?: UnreadAtOpen;
  /** The channel the composer starts on where it can (`?channel=email`). */
  preferChannel?: Channel;
  panelOpen: boolean;
  onPanel: (open: boolean) => void;
  onBack: () => void;
  onStar: (starred: boolean) => void;
  onRead: (read: boolean) => void;
  onDelete: () => void;
}) {
  const { user } = useAuth();
  const [filter, setFilter] = React.useState<ActivityFilter>("all");
  React.useEffect(() => setFilter("all"), [c.id]);
  const title = conversationTitle(c);
  // Shared with the page's own read of it, so opening a thread asks once.
  const contactRead = useContact(c.leadId ?? undefined);
  const contact = contactRead.data;
  const pending = contactRead.isPending;
  const label = useOutcomeLabel(contact);
  const record = React.useMemo(() => ({ contact, label, pending }), [contact, label, pending]);

  return (
    <RecordContext.Provider value={record}>
      <ConversationIdContext.Provider value={live ? c.id : null}>
      <section className="flex min-w-0 flex-1 flex-col" aria-label={`Conversation with ${title}`}>
        <ThreadHeader
          conversation={c}
          live={live}
          texts={texts}
          organize={organize}
          contact={contact}
          filter={filter}
          onFilter={setFilter}
          panelOpen={panelOpen}
          onPanel={onPanel}
          onBack={onBack}
          onStar={onStar}
          onRead={onRead}
          onDelete={onDelete}
        />
        {contact?.callback && seesNextCall(user?.role) && (
          <div className="border-b border-border bg-surface px-3 py-2">
            <OpenCallback contact={contact} />
          </div>
        )}
        {contact && (
          <NextAppointment key={`next:${contact.id}`} leadId={contact.id} className="border-b border-border bg-surface px-3 py-2" />
        )}
        {c.leadId ? (
          <RecordTimeline
            key={c.leadId}
            leadId={c.leadId}
            conversationId={c.id.startsWith("lead:") ? undefined : c.id}
            contactName={title}
            filter={filter}
            unreadAtOpen={unreadAtOpen}
          />
        ) : live ? (
          <TextTimeline key={c.id} conversationId={c.id} contactName={title} filter={filter} unreadAtOpen={unreadAtOpen} />
        ) : (
          <Timeline conversation={c} filter={filter} newFrom={newFrom} />
        )}
        <Composer conversation={c} live={live} preferChannel={preferChannel} />
      </section>
      </ConversationIdContext.Provider>
    </RecordContext.Provider>
  );
}

/**
 * A conversation's texts and the composer under them, without the thread's header, the record's
 * pinned callback and appointment, or its calls and notes: what the Phone page shows beside a
 * call (ACL-561, `softphone/call-texts.tsx`), where the call's card already says all of that.
 *
 * The texts are the conversation's own (`TextTimeline`), so each picture is fetched through the
 * conversation it arrived in and says so while it is still arriving or when it could not be kept,
 * as it does here. A contact's thread before their first text (`recordConversation`) has nothing
 * to read yet; the composer sends that first text from their record.
 */
export function ConversationTexts({ conversation: c }: { conversation: Conversation }) {
  const title = conversationTitle(c);
  const first = c.id.startsWith("lead:");
  return (
    <ConversationIdContext.Provider value={first ? null : c.id}>
      {first ? (
        <EmptyState
          className="min-h-0 flex-1 bg-surface-sunken"
          title="No texts yet"
          description={`Texts with ${title}, and the pictures they send, appear here.`}
        />
      ) : (
        <TextTimeline key={c.id} conversationId={c.id} contactName={title} filter="all" unreadAtOpen={undefined} />
      )}
      <Composer conversation={c} live />
    </ConversationIdContext.Provider>
  );
}

/**
 * The header's Call button (ACL-443).
 *
 * `DialNumberButton` renders plain text when there is nothing to dial — no endpoint on the
 * account, or a number that is not one — and with an icon as its child that would leave a
 * bordered box holding a phone glyph that answers no click. So the button is drawn only when it
 * would really dial, and the number in the subtitle is what remains otherwise.
 *
 * `useSoftphone` is read here rather than in the header so the header keeps one concern.
 */
function CallContactButton({
  phone: number,
  name,
  lead,
}: {
  phone: string | null;
  name?: string | null;
  lead?: LeadCard | undefined;
}) {
  const phone = useSoftphone();
  if (!canCallContact(number, phone.available, isDialable)) return null;
  return (
    <Tooltip content="Call">
      <DialNumberButton
        number={number}
        name={name}
        lead={lead}
        className="grid size-8 shrink-0 place-items-center rounded-md border border-border text-text-muted transition-colors hover:bg-surface-hover hover:text-accent-11"
      >
        <Phone className="size-4" />
      </DialNumberButton>
    </Tooltip>
  );
}

const FILTER_OPTIONS: { value: ActivityFilter; label: string }[] = [
  { value: "all", label: "All activity" },
  ...ACTIVITY_TYPES.map((t) => ({ value: t.type as ActivityFilter, label: t.label })),
];

function ThreadHeader({
  conversation: c, live, texts, organize, contact, filter, onFilter, panelOpen, onPanel, onBack, onStar, onRead, onDelete,
}: {
  conversation: Conversation;
  live: boolean;
  texts: boolean;
  organize: boolean;
  contact: LeadCard | undefined;
  filter: ActivityFilter;
  onFilter: (filter: ActivityFilter) => void;
  panelOpen: boolean;
  onPanel: (open: boolean) => void;
  onBack: () => void;
  onStar: (starred: boolean) => void;
  onRead: (read: boolean) => void;
  onDelete: () => void;
}) {
  const [deleting, setDeleting] = React.useState(false);
  const [outcome, setOutcome] = React.useState(false);
  const [booking, setBooking] = React.useState(false);
  const title = conversationTitle(c);
  const unread = c.unread > 0;
  // An email thread is an address, not a number: the phone to call is the record's (ADR-180).
  const email = c.channel === "email";
  const phone = email ? contact?.phone ?? null : c.phone;
  // Texts and emails are both the timeline's messages, which a core-api without texts refuses.
  const only = c.leadId ? RECORD_FILTERS.filter((f) => texts || (f !== "sms" && f !== "email")) : live ? TEXT_FILTERS : null;
  const options = only ? FILTER_OPTIONS.filter((o) => only.includes(o.value)) : FILTER_OPTIONS;
  const current = FILTER_OPTIONS.find((o) => o.value === filter)!;
  const FilterIcon = filter === "all" ? MessagesSquare : typeIcon(filter);
  return (
    <header className="flex items-center gap-2 border-b border-border px-3 py-2">
      <Button variant="ghost" size="icon-sm" className="lg:hidden" onClick={onBack} aria-label="Back to conversations">
        <ArrowLeft />
      </Button>
      <Avatar name={c.contactName} className="size-8" />
      <div className="min-w-0 flex-1">
        <h2 className="truncate text-sm font-semibold text-text">
          {c.leadId ? (
            <Link to={`/contacts/${c.leadId}`} className="hover:underline">
              {title}
            </Link>
          ) : (
            title
          )}
        </h2>
        {/* The number reads as text now that the header has a button for calling it: two ways to
            dial, a few pixels apart, is one of them a mis-click (ACL-443). */}
        <p className="truncate text-xs text-text-muted">
          {email
            ? c.email ?? "Address not shown"
            : c.phone ? formatPhoneNumber(c.phone) : c.email ?? "No phone number"}
        </p>
      </div>

      {/* Calling the person you are texting, without leaving the thread to find the dialler
          (ACL-443). It is `DialNumberButton` as an icon, so the campaign it dials through, the
          reason it cannot dial, and the confirmation are the ones every other number on the
          console already gives — a click here rings a real telephone, and that is worth a
          sentence first. It sits before Set outcome because calling comes before recording how
          the call went. */}
      <CallContactButton phone={phone} name={c.contactName} lead={contact} />

      {contact && (
        <>
          <Button variant="outline" size="sm" aria-label="Set outcome" onClick={() => setOutcome(true)}>
            <PhoneCall />
            <span className="hidden sm:inline">Set outcome</span>
          </Button>
          <ContactOutcomeDialog open={outcome} onOpenChange={setOutcome} contact={contact} />
          <Tooltip content="Book appointment">
            <Button variant="outline" size="sm" aria-label="Book appointment" onClick={() => setBooking(true)}>
              <CalendarPlus />
            </Button>
          </Tooltip>
          <NewAppointmentDialog
            open={booking}
            onOpenChange={setBooking}
            contact={{
              id: contact.id,
              name: contactName(contact) ?? contact.phone,
              phone: contact.phone,
              timezone: contact.clock_timezone ?? contact.timezone,
            }}
            source="conversation"
          />
        </>
      )}

      {/* An icon and a chevron, not the label: the label took the room the contact's name needs
          (ACL-592). Narrowed, the icon becomes that kind's own in the accent, so the header still
          says what the thread shows; the tooltip says it in words. */}
      <DropdownMenu>
        <Tooltip content={`Show: ${current.label}`}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Show: ${current.label}`}
              className={cn("gap-0.5 px-1.5", filter !== "all" && "text-accent-11")}
            >
              <FilterIcon />
              <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
        </Tooltip>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Show in the thread</DropdownMenuLabel>
          {options.map((o) => (
            <DropdownMenuItem key={o.value} onSelect={() => onFilter(o.value)}>
              <Check className={cn(o.value !== filter && "invisible")} />
              {o.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {organize && (
        <Tooltip content={c.starred ? "Unstar" : "Star"}>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-pressed={c.starred}
            aria-label={c.starred ? "Unstar conversation" : "Star conversation"}
            onClick={() => onStar(!c.starred)}
            className={cn(c.starred && "text-warning-9")}
          >
            <Star className={cn(c.starred && "fill-current")} />
          </Button>
        </Tooltip>
      )}
      <Tooltip content={unread ? "Mark as read" : "Mark as unread"}>
        <Button variant="ghost" size="icon-sm" aria-label={unread ? "Mark as read" : "Mark as unread"} onClick={() => onRead(unread)}>
          {unread ? <MailOpen /> : <Mail />}
        </Button>
      </Tooltip>
      {organize && (
        <Tooltip content="Delete conversation">
          <Button variant="ghost" size="icon-sm" aria-label="Delete conversation" onClick={() => setDeleting(true)}>
            <Trash2 />
          </Button>
        </Tooltip>
      )}
      <Tooltip content={panelOpen ? "Hide contact details" : "Show contact details"}>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-pressed={panelOpen}
          aria-label={panelOpen ? "Hide contact details" : "Show contact details"}
          onClick={() => onPanel(!panelOpen)}
        >
          {panelOpen ? <PanelRightClose /> : <PanelRightOpen />}
        </Button>
      </Tooltip>

      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title="Delete this conversation?"
        description={`The conversation with ${title} leaves every inbox. Its messages, calls and recordings are kept on the contact's record.`}
        confirmLabel="Delete conversation"
        destructive
        onConfirm={() => {
          setDeleting(false);
          onDelete();
        }}
      />
    </header>
  );
}

function Timeline({
  conversation: c, filter, newFrom,
}: {
  conversation: Conversation;
  filter: ActivityFilter;
  newFrom: number | null;
}) {
  const end = React.useRef<HTMLDivElement>(null);
  const shown = filterActivities(c.activities, filter);
  const title = conversationTitle(c);
  // A thread opens on its newest activity, as a phone's does.
  React.useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [c.id, filter]);

  if (shown.length === 0) {
    return (
      <EmptyState
        className="min-h-0 flex-1 bg-surface-sunken"
        title={filter === "all" ? "No activity yet" : "Nothing of this kind in the thread"}
        description={filter === "all" ? "Messages, calls and comments with this contact appear here." : "Pick All activity from the filter in the header to see everything."}
      />
    );
  }
  const newId = newFrom !== null ? c.activities[newFrom]?.id : undefined;
  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-surface-sunken px-4 py-4 scrollbar-subtle" aria-live="polite">
      <ActivityRows activities={shown} contactName={title} newId={newId} />
      <div ref={end} />
    </div>
  );
}

/**
 * How often the open thread asks again. Your own writes and the `leads` hint already refetch it;
 * this catches what nothing hints for: another rep's note, a call that ended without an outcome.
 */
const THREAD_POLL = 30_000;

/** How close to the bottom, in pixels, still counts as reading the newest. */
const AT_BOTTOM_PX = 48;

/**
 * The text the "New" line sits above, in a thread read from core-api. It is placed once the thread
 * holds what was unread when it was opened, and then stays there while the thread is open: a text
 * that lands, a refetch or an older page does not move it. A new `unreadAtOpen` (the page took
 * another opening) places it again. `conversationId` is the opened conversation, whose messages
 * alone were unread: the record's timeline holds the contact's other threads too.
 */
function useNewLine(
  activities: readonly Activity[],
  unreadAtOpen: UnreadAtOpen | undefined,
  conversationId?: string,
): string | undefined {
  const [placed, setPlaced] = React.useState<{ from: UnreadAtOpen | undefined; id: string | undefined } | null>(null);
  if (placed ? placed.from === unreadAtOpen : unreadAtOpen === undefined) return placed?.id;
  const id = firstNewText(activities, unreadAtOpen, conversationId);
  if (id === null) return undefined;
  // Kept during the render, as React's "adjusting state when a prop changes": no frame without it.
  setPlaced({ from: unreadAtOpen, id });
  return id;
}

/** A contact's thread from their record's timeline: everything, the header's filter narrowing it on
 *  the server. The SMS and Email filters ask for the timeline's messages of their channel only. */
function RecordTimeline({
  leadId, conversationId, contactName, filter, unreadAtOpen,
}: {
  leadId: string;
  /** The conversation opened, when it is one core-api has: whose unread messages the "New" line marks. */
  conversationId: string | undefined;
  contactName: string;
  filter: ActivityFilter;
  unreadAtOpen: UnreadAtOpen | undefined;
}) {
  const ask = timelineKinds(filter);
  const kinds = ask?.kinds ?? "";
  const timeline = useContactTimeline(leadId, kinds, { refetchInterval: THREAD_POLL, channel: ask?.channel });
  const pages = timeline.data?.pages;
  const { label, pending } = React.useContext(RecordContext);
  const channel = ask?.channel;
  const activities = React.useMemo(() => {
    const all = threadActivities(pages ?? [], label);
    // A core-api before ADR-180 does not know `channel=` and sends every message: the other
    // channel's are left out here as well, so the Email filter never lists a text.
    return channel ? all.filter((a) => a.kind !== "message" || a.channel === channel) : all;
  }, [pages, label, channel]);
  const newId = useNewLine(activities, unreadAtOpen, conversationId);
  // The record and its timeline answer 404 alike for a lead the reader may not open, and the page
  // then draws the conversation's own texts instead. Until the record has answered, the timeline's
  // 404 waits for that rather than flashing an error.
  if (pending && leadNotFound(timeline.error)) return <ThreadLoading />;
  return (
    <PagedThread
      query={timeline}
      activities={activities}
      view={`${kinds}:${channel ?? ""}`}
      contactName={contactName}
      newId={newId}
      empty={filter === "all" ? "Calls, texts, notes and outcomes with this contact appear here." : null}
    />
  );
}

/** The messages of a conversation with a sender the platform has no record for: they are all there
 *  is. One conversation is one channel, so the SMS and Email filters keep all of it or none. */
function TextTimeline({
  conversationId, contactName, filter, unreadAtOpen,
}: {
  conversationId: string;
  contactName: string;
  filter: ActivityFilter;
  unreadAtOpen: UnreadAtOpen | undefined;
}) {
  const texts = useTextMessages(conversationId);
  const pages = texts.data?.pages;
  const activities = React.useMemo(
    () => (pages ?? []).flatMap((p) => p.messages.map((m) => messageActivity(m))).reverse(),
    [pages],
  );
  const newId = useNewLine(activities, unreadAtOpen, conversationId);
  const shown = React.useMemo(() => filterActivities(activities, filter), [activities, filter]);
  return (
    <PagedThread
      query={texts}
      activities={shown}
      view={`texts:${filter}`}
      contactName={contactName}
      newId={newId}
      empty={filter === "all" ? "Messages with this sender appear here." : null}
    />
  );
}

/**
 * A thread read a page at a time from core-api, oldest first. It opens on the newest, as a phone's
 * does; "Earlier" at the top pages further back and leaves what you were reading where it was.
 * Something new landing at the bottom (a refetch after a hint) follows you down only if you were
 * already there, so reading back up the thread is never yanked away.
 */
function PagedThread({
  query, activities, view, contactName, newId, empty,
}: {
  query: Pick<
    UseInfiniteQueryResult,
    "isPending" | "isError" | "error" | "refetch" | "hasNextPage" | "isFetchingNextPage" | "fetchNextPage"
  >;
  activities: readonly Activity[];
  /** What is shown (a filter): a new one opens at the bottom again. */
  view: string;
  contactName: string;
  /** The first unread text, for the "New" line. */
  newId?: string;
  /** The empty state's line; null for a filter that found nothing. */
  empty: string | null;
}) {
  const scroller = React.useRef<HTMLDivElement>(null);
  // Distance from the bottom when "Earlier" was pressed: restored once the older page is in.
  const fromBottom = React.useRef<number | null>(null);
  const atBottom = React.useRef(true);
  const landed = React.useRef<string | null>(null);

  React.useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || activities.length === 0) return;
    if (landed.current !== view) {
      landed.current = view;
      el.scrollTop = el.scrollHeight;
    } else if (fromBottom.current !== null) {
      el.scrollTop = el.scrollHeight - fromBottom.current;
      fromBottom.current = null;
    } else if (atBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [activities, view]);

  const earlier = () => {
    const el = scroller.current;
    if (el) fromBottom.current = el.scrollHeight - el.scrollTop;
    void query.fetchNextPage();
  };

  if (query.isPending) return <ThreadLoading />;
  if (query.isError) {
    return (
      <div className="min-h-0 flex-1 bg-surface-sunken p-4">
        <ErrorState
          error={query.error}
          title="Could not load the thread"
          action={
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }
  if (activities.length === 0 && !query.hasNextPage) {
    return (
      <EmptyState
        className="min-h-0 flex-1 bg-surface-sunken"
        title={empty !== null ? "No activity yet" : "Nothing of this kind in the thread"}
        description={empty ?? "Pick All activity from the filter in the header to see everything."}
      />
    );
  }
  return (
    <div
      ref={scroller}
      onScroll={(e) => {
        const el = e.currentTarget;
        atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < AT_BOTTOM_PX;
      }}
      className="min-h-0 flex-1 overflow-y-auto bg-surface-sunken px-4 py-4 scrollbar-subtle"
      aria-live="polite"
    >
      {query.hasNextPage && (
        <div className="mb-3 flex justify-center">
          <Button variant="outline" size="sm" loading={query.isFetchingNextPage} onClick={earlier}>
            Earlier
          </Button>
        </div>
      )}
      <ActivityRows activities={activities} contactName={contactName} newId={newId} live />
    </div>
  );
}

function ThreadLoading() {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center bg-surface-sunken" role="status">
      <Spinner className="size-5" />
      <span className="sr-only">Loading the thread</span>
    </div>
  );
}
